import { useRef, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button, ModalButtonWithForm } from 'components/common';
import {
  Datepicker,
  EmailMessageEditor,
  FormGroup,
  NumberStepper,
  Password,
  RadioGroup
} from 'components/common/form';
import { addDays } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { UsersCreateMultiple, UsersCreateSingle } from '.';

import type { FormApi, FormState } from 'informed';
import type { EmailTemplateData } from 'shared-types';

type FormValues = {
  email?: string;
  emails?: string[];
  firstName?: string;
  lastName?: string;
  newEmail?: string;
  password?: string;
  passwordExpirationInDays: number;
  registrationPassword: boolean;
  userExpirationDate?: Date | null;
  username?: string;
  usernames?: string[];
  userSelector: 'useEmail' | 'useUsername';
};

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: (params: { showLoading: boolean }) => Promise<void>;
}
const UsersCreate = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers
}: Props) => {
  const { dispatch } = useDispatch();
  const { language, messages, serverConfig, theme } = useReduxState();
  const { dateNow } = useDateNow();

  const [createSingle, setCreateSingle] = useState(true);
  const [disableSubmit, setDisableSubmit] = useState(true);

  const formApi = useRef<FormApi<FormValues>>(null);
  const inputContainer = useRef<HTMLDivElement>(null);

  const handleCreateSingleChange = (value: boolean) => {
    setCreateSingle(value);
    formApi.current?.setValue('registrationPassword', false);
  };

  const handleModalVisible = () => {
    const { userSelector } = formApi.current?.getState().values;

    if (userSelector === 'useUsername') {
      formApi.current?.setValue('registrationPassword', true);
    }
    inputContainer.current?.querySelector('input').focus();
  };

  const handleRegistrationPasswordChange = (value: boolean) => {
    if (!value) {
      const values = formApi.current?.getState().values;
      if (values?.password) formApi.current?.setValue('password', '');

      const errors = formApi.current?.getState().errors;
      if (errors?.password) formApi.current?.setError('password', undefined);
    }
  };

  const handleSubmitDisabledStateChange = () => {
    const { errors, values } = formApi.current?.getState();
    const { userSelector } = values;

    let field: string | string[];
    if (!userSelector || userSelector === 'useEmail') {
      field = values.newEmail || values.emails;
    } else field = values.username || values.usernames;

    const disable =
      errors || Array.isArray(field) ? !field?.length : !field?.trim();
    if (disable !== disableSubmit) setDisableSubmit(disable);
  };

  const handleUserSelectorChange = (value: 'useUsername' | 'useEmail') => {
    if (value === 'useUsername') {
      formApi.current?.setValue('registrationPassword', true);
    } else formApi.current?.setValue('registrationPassword', false);
  };

  const setFormApi = (api: FormApi<FormValues>) => (formApi.current = api);

  const setInputContainer = (el: HTMLDivElement) =>
    (inputContainer.current = el);

  const submit = async (values) => {
    const response = await api.post('/admin/users', {
      createMultiple: !createSingle,
      emailContent: values.email,
      emails: values.newEmail
        ? [values.newEmail.trim()]
        : values.emails || null,
      firstName: values.firstName ? values.firstName.trim() : null,
      language,
      lastName: values.lastName ? values.lastName.trim() : null,
      password: values.registrationPassword ? values.password : '',
      passwordExpirationInDays: values.passwordExpirationInDays,
      userExpirationDate: values.userExpirationDate
        ? toDate(values.userExpirationDate, { timeZone: serverConfig.timeZone })
        : null,
      usernames: values.username
        ? [values.username?.trim()]
        : values.usernames || null
    });

    await updateUsers({ showLoading: false });

    if (response.data.accountsCreated > 0) {
      dispatch.addNotice({
        text: createSingle
          ? messages.usersCreateUserSuccess
          : `${response.data.accountsCreated} ${messages.settingsAddUsersAccountsCreated}`,
        type: 'success'
      });
    }

    if (
      response.data.errors.email_taken.length ||
      response.data.errors.username_taken.length
    ) {
      dispatch.addNotice({
        text: `${
          values.emails
            ? messages.authEmailInUseError
            : messages.authUsernameInUseError
        }:<br /> ${
          values.emails
            ? response.data.errors.email_taken.join(',<br />')
            : response.data.errors.username_taken.join(',<br />')
        }`,
        type: 'warning'
      });
    }

    if (response.data.errors.email_as_username.length) {
      dispatch.addNotice({
        text: `${
          messages.authInvalidEmailAsUsername
        }:<br /> ${response.data.errors.email_as_username.join(',<br />')}`,
        type: 'warning'
      });
    }
  };

  const initialValues = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    passwordExpirationInDays:
      serverConfig.passwordManuallySetExpirationTimeInDays,
    registrationPassword: false,
    userExpirationDate: null
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonStyle={{
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        marginRight: 1
      }}
      buttonText={messages.usersCreateUser}
      initialValues={initialValues}
      modalActionText={messages.commonCreate}
      modalTitle={messages.usersCreateUser}
      modalWidth="wide"
      onModalVisible={handleModalVisible}
      onSubmit={submit}
      pristineIsValid={false}
      setFormApi={setFormApi}
      submitDisabled={disableSubmit}
    >
      {(formState: FormState<FormValues>) => (
        <>
          <div className="mode-container">
            <Button
              category="light"
              icon={createSingle ? 'check-circle' : 'circle'}
              iconStyle={!createSingle && { opacity: 0.5 }}
              onClick={() => handleCreateSingleChange(true)}
              style={{
                ...(createSingle
                  ? {
                      backgroundColor: theme.lightGreyDarkActive,
                      cursor: 'default',
                      pointerEvents: 'none'
                    }
                  : {
                      backgroundColor: theme.lightGrey
                    })
              }}
              text={messages.usersCreateUserSingle}
            />

            <Button
              category="light"
              icon={!createSingle ? 'check-circle' : 'circle'}
              iconStyle={createSingle && { opacity: 0.5 }}
              onClick={() => handleCreateSingleChange(false)}
              style={{
                ...(!createSingle
                  ? {
                      backgroundColor: theme.lightGreyDarkActive,
                      cursor: 'default',
                      pointerEvents: 'none'
                    }
                  : {
                      backgroundColor: theme.lightGrey
                    })
              }}
              text={messages.usersCreateUserMultiple}
            />

            <style jsx>{`
              .mode-container {
                display: flex;
                flex-direction: row;
                gap: 10px;
                padding-bottom: 10px;
              }
            `}</style>
          </div>

          {createSingle ? (
            <UsersCreateSingle
              containerRef={setInputContainer}
              formApi={formApi.current}
              formState={formState}
              onUserSelectorChange={handleUserSelectorChange}
              onValueChange={handleSubmitDisabledStateChange}
            />
          ) : (
            <UsersCreateMultiple
              formApi={formApi.current}
              onUserSelectorChange={handleUserSelectorChange}
              onValueChange={handleSubmitDisabledStateChange}
            />
          )}

          {serverConfig.accountAddForceExpirationDate && (
            <FormGroup
              htmlFor="userExpirationDate"
              label={messages.commonExpirationDateForNewUserAccounts}
              maxWidth="100%"
            >
              <Datepicker
                field="userExpirationDate"
                id="userExpirationDate"
                isClearable={false}
                maxDate={addDays(
                  dateNow,
                  serverConfig.accountAddForceExpirationDateMaxDays
                )}
                minDate={dateNow}
                required={true}
                validateOnChange={true}
                validateOnBlur={true}
                style={{ maxWidth: 200 }}
                placeholder={messages.commonExpirationDate}
              />
            </FormGroup>
          )}

          <FormGroup label={messages.commonRegistration} maxWidth="100%">
            <RadioGroup
              field="registrationPassword"
              onChange={handleRegistrationPasswordChange}
              options={[
                {
                  disabled: formState.values.userSelector === 'useUsername',
                  id: `registration-email`,
                  label: messages.usersRegistrationEmail,
                  value: false
                },
                {
                  id: `registration-password`,
                  label: messages.usersRegistrationPassword,
                  value: true
                }
              ]}
            />
          </FormGroup>

          {formState.values.registrationPassword ? (
            <>
              <FormGroup label={messages.commonPassword} maxWidth="100%">
                <Password
                  autoComplete="new-password"
                  field="password"
                  formApi={formApi.current}
                  id="password"
                  placeholder={messages.commonPassword}
                  required={formState.values.registrationPassword}
                  showGenerateOption={true}
                  style={{ maxWidth: 280 }}
                  userEmailAddress={formState.values.newEmail || ''}
                  userUsername={formState.values.username || ''}
                  validateNewPassword={true}
                  onValueChange={handleSubmitDisabledStateChange}
                />
              </FormGroup>

              {!serverConfig.passwordDoNotForcePasswordChange && (
                <NumberStepper
                  field="passwordExpirationInDays"
                  id={`password-expiration-in-days-create-user`}
                  label={messages.siteSettingsPasswordExpirationTimeInDays}
                  max={100000}
                  min={serverConfig.passwordManuallySetExpirationTimeInDays}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
              )}
            </>
          ) : null}

          {!formState.values.registrationPassword ? (
            <EmailMessageEditor
              availableTemplates={emailTemplates}
              id="create-new-user"
              style={{ marginTop: 20 }}
            />
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default UsersCreate;
